/**
 * City push-subscription helpers, shared by the SPA (web-push.js) and the
 * server-rendered pages (seo-push.js).
 *
 * Pure subscribe/unsubscribe/sync against the /api/push/city/* endpoints plus
 * the localStorage mirror. Token handling differs between callers (the SPA
 * reads its stored token; the SEO module may enable push first), so these take
 * the token explicitly and stay agnostic about where it came from.
 */

import { STORAGE_KEYS } from './config.js';

export function getFollowedCities() {
    const stored = localStorage.getItem(STORAGE_KEYS.webPushCities);
    if (!stored) return [];
    try {
        const arr = JSON.parse(stored);
        return Array.isArray(arr) ? arr : [];
    } catch {
        return [];
    }
}

export function setFollowedCities(cities) {
    if (!cities || cities.length === 0) {
        localStorage.removeItem(STORAGE_KEYS.webPushCities);
    } else {
        localStorage.setItem(STORAGE_KEYS.webPushCities, JSON.stringify(cities));
    }
}

export function isCityFollowed(slug) {
    return getFollowedCities().includes(slug);
}

export async function subscribeCity(token, slug) {
    try {
        const resp = await fetch('/api/push/city/subscribe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, city_slug: slug }),
        });
        if (!resp.ok) return false;
        const cities = getFollowedCities();
        if (!cities.includes(slug)) {
            cities.push(slug);
            setFollowedCities(cities);
        }
        return true;
    } catch (err) {
        console.error('[push-cities] subscribe failed:', err);
        return false;
    }
}

export async function unsubscribeCity(token, slug) {
    try {
        const resp = await fetch('/api/push/city/unsubscribe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, city_slug: slug }),
        });
        if (!resp.ok) return false;
        setFollowedCities(getFollowedCities().filter(c => c !== slug));
        return true;
    } catch (err) {
        console.error('[push-cities] unsubscribe failed:', err);
        return false;
    }
}

export async function syncCitySubscriptions(token) {
    try {
        const resp = await fetch(`/api/push/city/subscriptions?token=${encodeURIComponent(token)}`);
        if (resp.ok) {
            const data = await resp.json();
            setFollowedCities(data.cities || []);
        }
    } catch (err) {
        console.error('[push-cities] sync failed:', err);
    }
}
